import { useEffect, useState } from "react";
import { Volume2, VolumeX, Bell, Moon, EyeOff, Globe, LinkIcon, Copy, LogOut, Eye } from "lucide-react";
import { api } from "../lib/api";
import { useStore, store } from "../lib/store";
import type { Settings } from "@glass/shared";
import { Avatar } from "./Avatar";

interface SettingsPanelProps { onLogout: () => void; }

export function SettingsPanel({ onLogout }: SettingsPanelProps) {
  const me = useStore((s) => s.user);
  const settings = useStore((s) => s.settings);
  const [username, setUsername] = useState(me?.username || "");
  const [saving, setSaving] = useState(false);
  const [usernameMsg, setUsernameMsg] = useState<string | null>(null);

  useEffect(() => { if (me?.username) setUsername(me.username); }, [me?.username]);

  async function patch(p: Partial<Settings>) {
    setSaving(true);
    try {
      const r = await api.updateSettings(p);
      store.set({ settings: r.settings });
    } finally { setSaving(false); }
  }

  async function saveUsername() {
    setUsernameMsg(null);
    try {
      await api.setUsername(username);
      setUsernameMsg("Имя пользователя сохранено");
      const r = await api.me();
      store.set({ user: r.user });
    } catch (e) {
      setUsernameMsg(e instanceof Error ? e.message : String(e));
    }
  }

  async function toggleStealth() {
    const next = !settings?.stealth;
    await patch({ stealth: next });
    await api.setPresence(true, next);
    const r = await api.me();
    store.set({ user: r.user });
  }

  function copyShare() {
    if (!me?.share_link) return;
    const url = `${window.location.origin}/u/${me.share_link}`;
    navigator.clipboard.writeText(url).catch(() => 0);
  }

  if (!me) return null;

  return (
    <div className="h-full overflow-y-auto px-4 md:px-8 py-6">
      <div className="max-w-3xl mx-auto space-y-5">
        <section className="glass p-5 flex items-center gap-4">
          <Avatar name={me.name} url={me.avatar_url} size={64} online={!me.stealth && me.online} />
          <div className="flex-1 min-w-0">
            <div className="font-display text-xl font-bold truncate">{me.name}</div>
            <div className="text-sm text-ink-300 truncate">{me.email}</div>
            {me.username && <div className="text-sm text-accent-200">@{me.username}</div>}
          </div>
          <button className="btn btn-danger" onClick={onLogout}><LogOut size={14} /> Выйти</button>
        </section>

        <section className="glass p-5 space-y-3">
          <h3 className="font-semibold">Имя пользователя</h3>
          <div className="flex items-center gap-2">
            <span className="text-ink-400">@</span>
            <input className="glass-input flex-1" placeholder="username" value={username} onChange={(e) => setUsername(e.target.value.replace(/[^a-z0-9_]/gi, ""))} />
            <button className="btn btn-primary" onClick={saveUsername}>Сохранить</button>
          </div>
          {usernameMsg && <div className="text-xs text-ink-300">{usernameMsg}</div>}
        </section>

        <section className="glass p-5 space-y-2">
          <h3 className="font-semibold">Звуки</h3>
          <Toggle label="Звук отправки" icon={<Volume2 size={16} />} value={settings?.send_sound ?? true} onChange={(v) => patch({ send_sound: v })} disabled={saving} />
          <Toggle label="Звук получения" icon={<Bell size={16} />} value={settings?.receive_sound ?? true} onChange={(v) => patch({ receive_sound: v })} disabled={saving} />
          <Toggle label="Звук уведомлений" icon={<Bell size={16} />} value={settings?.notification_sound ?? true} onChange={(v) => patch({ notification_sound: v })} disabled={saving} />
        </section>

        <section className="glass p-5 space-y-2">
          <h3 className="font-semibold">Приватность</h3>
          <Toggle label="Stealth-режим (онлайн скрыт)" icon={settings?.stealth ? <EyeOff size={16} /> : <Eye size={16} />} value={settings?.stealth ?? false} onChange={() => toggleStealth()} disabled={saving} />
          {settings?.stealth && (
            <div className="text-xs text-ink-400 leading-relaxed">
              Другие пользователи не видят, что вы онлайн или печатаете. «Был в сети» отображается как «давно».
            </div>
          )}
        </section>

        <section className="glass p-5 space-y-2">
          <h3 className="font-semibold">Внешний вид</h3>
          <div className="flex items-center gap-2">
            <Moon size={16} />
            <span>Тема</span>
            <div className="ml-auto flex gap-1">
              {(["light", "dark", "system"] as const).map((t) => (
                <button key={t} className={`btn ${settings?.theme === t ? "btn-primary" : "btn-ghost"}`} onClick={() => patch({ theme: t })}>
                  {t === "light" ? "Светлая" : t === "dark" ? "Тёмная" : "Системная"}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Globe size={16} />
            <span>Язык</span>
            <select className="glass-input ml-auto" value={settings?.language || "ru"} onChange={(e) => patch({ language: e.target.value })}>
              <option value="ru">Русский</option>
              <option value="en">English</option>
            </select>
          </div>
        </section>

        <section className="glass p-5 space-y-2">
          <h3 className="font-semibold">Поделиться аккаунтом</h3>
          <div className="flex items-center gap-2 glass-row p-2">
            <LinkIcon size={14} className="text-ink-300 ml-2" />
            <code className="text-xs text-ink-200 truncate flex-1">{me.share_link ? `${window.location.origin}/u/${me.share_link}` : "—"}</code>
            <button className="btn btn-ghost" onClick={copyShare}><Copy size={14} /></button>
          </div>
        </section>

        <section className="glass p-5">
          <h3 className="font-semibold mb-2">О Glass</h3>
          <div className="text-sm text-ink-300 leading-relaxed">
            Glass Messenger — приватный мессенджер на Cloudflare Durable Objects. Турнстайл и Google-OAuth защищают от ботов; голосовые комнаты и Jitsi звонки работают из коробки. Версия beta.
          </div>
        </section>
      </div>
    </div>
  );
}

function Toggle({ label, icon, value, onChange, disabled }: { label: string; icon?: React.ReactNode; value: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className={`flex items-center gap-3 py-2 ${disabled ? "opacity-60" : "cursor-pointer"}`}>
      {icon}
      <span className="flex-1">{label}</span>
      <button
        type="button"
        aria-pressed={value}
        onClick={() => onChange(!value)}
        disabled={disabled}
        className={`w-12 h-7 rounded-full transition-colors relative ${value ? "bg-accent-500" : "bg-white/10"}`}>
        <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${value ? "translate-x-5" : ""}`} />
      </button>
      {value
        ? <span className="text-emerald-300 text-xs">вкл</span>
        : <span className="text-ink-400 text-xs">выкл</span>}
      <span className="sr-only">{value ? "Включено" : "Выключено"}</span>
      <span className="sr-only">{disabled ? "(сохранение)" : ""}</span>
      <span className="sr-only">{label}</span>
      <span className="sr-only"><VolumeX /></span>
    </label>
  );
}
